// client/src/pages/CheckinFormPage.jsx
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, AlertTriangle, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { useCheckinForm, useSubmitCheckin } from '../api/hooks'

const inputCls = `w-full px-3 py-2 text-sm border border-gray-200 rounded-lg
  text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-500 transition-colors bg-white`

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest bg-yellow-400 text-black rounded px-3 py-1.5 text-sm">
      {registration}
    </span>
  )
}

function SelectItem({ item, value, onChange }) {
  const opts = item.options || []
  const flagVals = item.flag_values || []
  const selected = value ?? ''

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {opts.map(opt => {
          const isFlagged = flagVals.includes(opt)
          const isSelected = selected === opt
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium border transition-all',
                isSelected
                  ? isFlagged
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-indigo-600 border-indigo-600 text-white'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300 bg-white'
              )}
            >
              {opt}
              {isFlagged && isSelected && (
                <AlertTriangle size={12} className="inline ml-1.5" />
              )}
            </button>
          )
        })}
      </div>
      {flagVals.includes(selected) && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle size={12} />
          This will be flagged for your garage to review
        </p>
      )}
    </div>
  )
}

function YesNoItem({ item, value, onChange }) {
  const flagVals = item.flag_values || []

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {['Yes', 'No'].map(opt => {
          const isFlagged = flagVals.includes(opt)
          const isSelected = value === opt
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={clsx(
                'flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all',
                isSelected
                  ? isFlagged
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'bg-indigo-600 border-indigo-600 text-white'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300 bg-white'
              )}
            >
              {opt}
            </button>
          )
        })}
      </div>
      {flagVals.includes(value) && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle size={12} />
          This will be flagged for your garage to review
        </p>
      )}
    </div>
  )
}

function CheckinItem({ item, value, onChange }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">
          {item.label}
          {item.is_required && <span className="text-red-500 ml-1">*</span>}
        </p>
        {item.hint && (
          <p className="text-xs text-gray-500 mt-0.5">{item.hint}</p>
        )}
      </div>

      {item.response_type === 'select' && (
        <SelectItem item={item} value={value} onChange={onChange} />
      )}
      {item.response_type === 'yesno' && (
        <YesNoItem item={item} value={value} onChange={onChange} />
      )}
      {item.response_type === 'number' && (
        <input
          type="number"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter number…"
          className={inputCls}
        />
      )}
      {item.response_type === 'text' && (
        <textarea
          rows={2}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Type here…"
          className={clsx(inputCls, 'resize-none')}
        />
      )}
    </div>
  )
}

export default function CheckinFormPage() {
  const { token } = useParams()
  const { data, isLoading, error } = useCheckinForm(token)
  const submit = useSubmitCheckin(token)

  const [mileage, setMileage]   = useState('')
  const [notes, setNotes]       = useState('')
  const [responses, setResponses] = useState({})
  const [submitted, setSubmitted] = useState(null)
  const [submitError, setSubmitError] = useState(null)

  const setResponse = (itemId, value) =>
    setResponses(prev => ({ ...prev, [itemId]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError(null)

    // Validate required items
    if (data?.items) {
      const missing = data.items.filter(i => i.is_required && !responses[i.id])
      if (missing.length) {
        setSubmitError(`Please answer all required items (${missing.length} remaining)`)
        return
      }
    }

    try {
      const payload = {
        mileage: mileage || null,
        notes:   notes   || null,
        responses: Object.entries(responses).map(([itemId, value]) => ({
          itemId,
          value,
        })),
      }
      const result = await submit.mutateAsync(payload)
      setSubmitted(result)
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Submission failed — please try again')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-gray-50 flex items-center justify-center p-4">
        <div className="text-gray-500 text-sm">Loading check-in…</div>
      </div>
    )
  }

  if (error) {
    const is404 = error.response?.status === 404
    const is410 = error.response?.status === 410
    return (
      <div className="min-h-dvh bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={24} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {is404 ? 'Link not found' : is410 ? 'Schedule inactive' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-gray-500">
            {is404
              ? 'This check-in link doesn\'t exist or has expired.'
              : is410
                ? 'This check-in schedule has been deactivated.'
                : 'Please try again or contact your garage.'}
          </p>
        </div>
      </div>
    )
  }

  if (submitted) {
    const hasFlagged = submitted.flaggedCount > 0
    return (
      <div className="min-h-dvh bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
          <div className={clsx(
            'w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4',
            hasFlagged ? 'bg-amber-100' : 'bg-green-100'
          )}>
            <CheckCircle size={24} className={hasFlagged ? 'text-amber-500' : 'text-green-500'} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check-in submitted</h1>
          <p className="text-gray-500 text-sm mb-4">
            Thanks for completing your vehicle check-in.
          </p>
          {hasFlagged && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
              <p className="text-sm font-semibold text-amber-800 mb-1">
                {submitted.flaggedCount} item{submitted.flaggedCount !== 1 ? 's' : ''} flagged
              </p>
              <p className="text-xs text-amber-700">
                Your garage has been notified and will follow up with you about the flagged items.
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  const { schedule, vehicle, categories } = data

  return (
    <div className="min-h-dvh bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                {schedule.templateName}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <Plate registration={vehicle.registration} />
                <span className="text-sm text-gray-700 font-medium">
                  {vehicle.make} {vehicle.model}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Intro */}
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6">
          <p className="text-sm text-indigo-800">
            This is your {schedule.frequency} health check-in
            {schedule.garageName ? ` from ${schedule.garageName}` : ''}.
            Complete each section below — anything unusual will be flagged for your garage.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mileage */}
          <div>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Mileage
            </h2>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Current odometer reading
              </label>
              <input
                type="number"
                min="0"
                value={mileage}
                onChange={e => setMileage(e.target.value)}
                placeholder="e.g. 45000"
                className={inputCls}
              />
            </div>
          </div>

          {/* Items grouped by category */}
          {Object.entries(categories).map(([category, items]) => (
            <div key={category}>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
                {category}
              </h2>
              <div className="space-y-3">
                {items.map(item => (
                  <CheckinItem
                    key={item.id}
                    item={item}
                    value={responses[item.id]}
                    onChange={val => setResponse(item.id, val)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* General notes */}
          <div>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Additional notes
            </h2>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Anything else your garage should know…"
                className={clsx(inputCls, 'resize-none')}
              />
            </div>
          </div>

          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submit.isPending}
            className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                       text-white text-base font-semibold rounded-xl transition-colors
                       flex items-center justify-center gap-2"
          >
            {submit.isPending ? 'Submitting…' : 'Submit Check-In'}
            {!submit.isPending && <ChevronRight size={18} />}
          </button>
        </form>
      </div>
    </div>
  )
}
